import React, { useState } from "react";

import { SEO } from "../../seo/SEO";
import styled from "styled-components";
import { useTypedResource } from "../../i18n/languagehandler";

const HeroSection = styled.section`
  max-width: 1200px;
  margin: 40px auto 0;
  padding: 0 20px;
`;

const HeroContent = styled.div`
  h1 {
    color: var(--text);
    font-size: 48px;
    margin: 0 0 16px 0;
    line-height: 1.2;
    text-transform: uppercase;
  }
  p {
    color: var(--text-secondary);
    font-size: 18px;
    line-height: 1.6;
    margin: 0;
  }
`;

// Removed HeroImage as requested

const CoursesGrid = styled.section`
  max-width: 1200px;
  margin: 60px auto 0;
  padding: 0 20px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  display: flex;
  flex-direction: column;
  height: 100%;
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
  }
`;

const CardImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
`;

const CardContent = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const CardTitle = styled.h3`
  color: var(--text);
  font-size: 20px;
  margin: 0 0 12px 0;
  line-height: 1.3;
`;

const CardDescription = styled.p`
  color: var(--text-secondary);
  font-size: 16px;
  line-height: 1.5;
  margin: 0 0 20px 0;
`;

const CardExtended = styled.div<{ isVisible: boolean }>`
  color: var(--text-secondary);
  font-size: 16px;
  line-height: 1.6;
  margin: 0 0 20px 0;
  max-height: ${(props) => (props.isVisible ? "1000px" : "0")};
  overflow: hidden;
  transition: max-height 0.3s ease, opacity 0.3s ease;
  opacity: ${(props) => (props.isVisible ? "1" : "0")};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: flex-start;
  margin-top: auto;
  padding-top: 16px;
`;

const PrimaryButton = styled.a`
  background: var(--primary);
  color: #fff;
  border: none;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  display: inline-block;
  &:hover {
    background: var(--primary-dark);
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(55, 90, 127, 0.3);
  }
`;

const SecondaryButton = styled.button`
  background: transparent;
  color: var(--primary);
  border: 2px solid var(--primary);
  padding: 10px 18px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover {
    background: var(--primary);
    color: #fff;
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(55, 90, 127, 0.3);
  }
`;

export const OnlineCourses: React.FC = () => {
  const res = useTypedResource<{
    title: string;
    intro: string;
    course1Title: string;
    course1Description: string;
    course1Url: string;
    course2Title: string;
    course2Description: string;
    course2Extended: string;
    course3Title: string;
    course3Description: string;
    course3Extended: string;
    course4Title: string;
    course4Description: string;
    course4Extended: string;
    enrollButton: string;
    learnMoreButton: string;
    showLess: string;
  }>("onlinecourses");

  const [expandedCards, setExpandedCards] = useState<number[]>([]);

  const toggleCard = (index: number) => {
    setExpandedCards((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  const courses = [
    {
      title: res.course1Title,
      description: res.course1Description,
      extended: "",
      image: "/assets/Online courses/adhd-skaermtid.png",
      url: res.course1Url,
    },
    {
      title: res.course2Title,
      description: res.course2Description,
      extended: res.course2Extended,
      image: "/assets/Online courses/Online koncentracija.jpeg",
      url: "/contact",
    },
    {
      title: res.course3Title,
      description: res.course3Description,
      extended: res.course3Extended,
      image: "/assets/Online courses/Online class agreements Picture.png",
      url: "/contact",
    },
    {
      title: res.course4Title,
      description: res.course4Description,
      extended: res.course4Extended,
      image: "/assets/Online courses/ONline predavanje autizam.jpeg",
      url: "/contact",
    },
  ];

  return (
    <main>
      <SEO title={res.title} description={res.intro} path="/online-courses" />

      <HeroSection>
        <HeroContent>
          <h1>{res.title}</h1>
          <p>{res.intro}</p>
        </HeroContent>
      </HeroSection>

      <CoursesGrid>
        {courses.map((course, index) => (
          <Card key={index}>
            <CardImage
              src={process.env.PUBLIC_URL + course.image}
              alt={course.title}
            />
            <CardContent>
              <CardTitle>{course.title}</CardTitle>
              <CardDescription>{course.description}</CardDescription>
              <CardExtended isVisible={expandedCards.includes(index)}>
                {course.extended}
              </CardExtended>
              <ButtonGroup>
                <PrimaryButton
                  href={course.url}
                  target={course.url.startsWith("http") ? "_blank" : "_self"}
                  rel="noopener noreferrer"
                >
                  {res.enrollButton}
                </PrimaryButton>
                {course.extended ? (
                  <SecondaryButton onClick={() => toggleCard(index)}>
                    {expandedCards.includes(index)
                      ? res.showLess
                      : res.learnMoreButton}
                  </SecondaryButton>
                ) : null}
              </ButtonGroup>
            </CardContent>
          </Card>
        ))}
      </CoursesGrid>
    </main>
  );
};
